// src/firebase/auth.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  sendPasswordResetEmail,
  sendEmailVerification,
  GoogleAuthProvider,
  signInWithPopup,
  reauthenticateWithCredential,
  linkWithCredential,
  deleteUser,
} from "firebase/auth";
import { ref, set, update, get, remove } from "firebase/database";
import { ref as storageRef, deleteObject } from "firebase/storage";
import { auth, db, storage } from "./config";
import { MODERATOR_UIDS } from "../constants/roles";

// ─── Yardımcı: Rastgele profil fotoğrafı seç (1-50 arası) ───────────────────
// Fotoğraflar public/profilfoto/ klasöründe olmalı — src/assets altında değil.
// public/ klasöründeki dosyalar build'de hash'lenmez, sabit URL kalır.
function randomDefaultPhoto() {
  const n = Math.floor(Math.random() * 72) + 1;
  return `/profilfoto/profilfoto${n}.png`;
}

// ─── Yardımcı: displayName → /usernames anahtarı ─────────────────────────────
// Büyük/küçük harf KORUNUR — "Dhmm" ve "dhmm" farklı iki anahtar.
// Firebase key olarak geçersiz karakterleri temizle (. # $ [ ] /)
export function toUsernameKey(name = "") {
  return name.trim().replace(/[.#$[\]/]/g, "");
}

export async function syncPublicProfile(firebaseUser, profileData = {}) {
  if (!firebaseUser?.uid) return;

  const createdAt = Number(profileData.createdAt);
  await set(ref(db, `publicProfiles/${firebaseUser.uid}`), {
    uid: firebaseUser.uid,
    displayName: profileData.displayName ?? firebaseUser.displayName ?? "",
    photoURL: profileData.photoURL ?? firebaseUser.photoURL ?? "",
    bio: profileData.bio ?? "",
    nationality: profileData.nationality ?? "",
    createdAt: Number.isFinite(createdAt) ? createdAt : 0,
  });
}

// ─── Yardımcı: photoURL Firebase Storage URL'si mi? ──────────────────────────
function isFirebaseStorageUrl(url = "") {
  return url.includes("firebasestorage.googleapis.com");
}

// ─── Yardımcı: Storage URL'den dosya yolunu çıkar ────────────────────────────
// "https://firebasestorage.googleapis.com/v0/b/<bucket>/o/profiles%2Fabc.jpg?..."
// → "profiles/abc.jpg"
function extractStoragePath(url = "") {
  try {
    const match = url.match(/\/o\/(.+?)(\?|$)/);
    if (!match) return null;
    return decodeURIComponent(match[1]);
  } catch (_) {
    return null;
  }
}

// ─── Yardımcı: E-posta domain kontrolü ───────────────────────────────────────
// Sadece yaygın ve güvenilir mail servislerine izin ver.
// Yazım hatası olan domainleri (gmail.co, gmail.a vb.) engeller.
const ALLOWED_EMAIL_DOMAINS = [
  // Google
  "gmail.com",
  // Microsoft
  "outlook.com", "hotmail.com", "live.com", "msn.com",
  // Yahoo
  "yahoo.com", "yahoo.co.uk", "yahoo.fr", "yahoo.de", "ymail.com",
  // Apple
  "icloud.com", "me.com", "mac.com",
  // Proton
  "proton.me", "protonmail.com", "pm.me",
  // Yandex
  "yandex.com", "yandex.ru", "ya.ru",
  // Zoho
  "zoho.com", "zohomail.com",
  // Diğer yaygın servisler
  "tutanota.com", "tutamail.com", "tuta.io",
  "fastmail.com", "fastmail.fm",
  "gmx.com", "gmx.net", "gmx.de",
  "mail.com",
  "aol.com",
  "hey.com",
  // Türk servisler
  "turknet.net.tr", "mynet.com", "superonline.com",
];

export function isAllowedEmailDomain(email = "") {
  const parts = email.trim().toLowerCase().split("@");
  if (parts.length !== 2) return false;
  const domain = parts[1];
  return ALLOWED_EMAIL_DOMAINS.includes(domain);
}

// ─── Ban kontrolü ─────────────────────────────────────────────────────────────
// Admin UID'leri ban kontrolünden muaf — engelleme silah olarak kullanılamaz.
export async function checkBlockStatus(firebaseUser) {
  if (!firebaseUser) return;
  if (MODERATOR_UIDS.includes(firebaseUser.uid)) return; // UID mühürlü admin → muaf

  const snap = await get(ref(db, `users/${firebaseUser.uid}`));
  if (!snap.exists()) return;

  const data    = snap.val();
  const blocked = data?.isBanned === true || data?.status === "blocked";
  if (blocked) {
    try { await signOut(auth); } catch (_) {}
    const err  = new Error("Bu hesap kalıcı olarak engellenmiştir.");
    err.code   = "auth/account-blocked";
    throw err;
  }
}

// ─── Kayıt ────────────────────────────────────────────────────────────────────
// AKIŞ:
//   1. /usernames/$key kontrol et        ← auth gerekmez (.read: true)
//   2. İsim alınmışsa hata fırlat        ← hiç Auth kaydı açılmaz
//   3. createUserWithEmailAndPassword     ← hesap açılır, auth != null olur
//   4. sendEmailVerification             ← doğrulama maili gönderilir
//   5. /users/$uid profili yaz           ← UID üzerinden admin mührü de set edilir
//   6. /usernames/$key mühürle
//   7. signOut                           ← doğrulama yapılmadan giriş engellemek için
export async function register({ email, password, displayName }) {
  const trimmedName = displayName.trim();
  const key         = toUsernameKey(trimmedName);

  // 0. Email domain kontrolü — yazım hatalı veya desteklenmeyen domainleri engelle
  if (!isAllowedEmailDomain(email)) {
    const err  = new Error("Bu e-posta servisi desteklenmiyor. Lütfen Gmail, Outlook, Yahoo gibi yaygın bir servis kullan.");
    err.code   = "__invalid_email_domain__";
    throw err;
  }

  // 1. İsim kontrolü — auth gerekmez
  if (key) {
    const nameSnap = await get(ref(db, `usernames/${key}`));
    if (nameSnap.exists()) {
      const err  = new Error("Bu isim zaten alınmış.");
      err.code   = "__name_taken__";
      throw err;
    }
  }

  // 2. Firebase Auth kaydı
  const { user } = await createUserWithEmailAndPassword(auth, email, password);

  // 3. Auth profiline displayName yaz
  try { await updateProfile(user, { displayName: trimmedName }); } catch (_) {}

  // 4. Doğrulama e-postası gönder
  try { await sendEmailVerification(user); } catch (_) {}

  // 5 & 6. DB kayıtları — admin kontrolü UID üzerinden yapılır
  const isAdmin = MODERATOR_UIDS.includes(user.uid);
  const photoURL = randomDefaultPhoto();
  const createdAt = Date.now();
  try {
    await set(ref(db, `users/${user.uid}`), {
      uid:           user.uid,
      email:         user.email,
      displayName:   trimmedName,
      photoURL,
      role:          isAdmin ? "admin" : "user",
      isAdmin,
      isBanned:      false,
      status:        "active",
      emailVerified: false,
      createdAt,
      lastLogin:     Date.now(),
      language:      "en",
      nationality:   "",
      bio:           "",
    });
    await syncPublicProfile(user, {
      displayName: trimmedName,
      photoURL,
      bio: "",
      nationality: "",
      createdAt,
    });
    if (key) {
      await set(ref(db, `usernames/${key}`), { uid: user.uid, displayName: trimmedName });
    }
  } catch (dbErr) {
    try { await remove(ref(db, `users/${user.uid}`)); } catch (_) {}
    try { await remove(ref(db, `publicProfiles/${user.uid}`)); } catch (_) {}
    try { await user.delete(); } catch (_) {}
    const err  = new Error("Veritabanına kaydedilemedi. Firebase kurallarını kontrol et.");
    err.code   = "__db_write_failed__";
    throw err;
  }

  // 7. Kayıt tamamlandı ama doğrulama bekleniyor — oturumu kapat
  try { await signOut(auth); } catch (_) {}

  const info  = new Error("Kayıt başarılı! Kaleye giriş yapabilmek için lütfen e-posta adresine gönderilen onay linkine tıkla.");
  info.code   = "__verify_email__";
  throw info;
}

// ─── E-posta / Şifre ile Giriş ────────────────────────────────────────────────
export async function login({ email, password }) {
  const { user } = await signInWithEmailAndPassword(auth, email, password);

  // Admin UID'leri e-posta doğrulama zorunluluğundan muaf
  if (!user.emailVerified && !MODERATOR_UIDS.includes(user.uid)) {
    try { await signOut(auth); } catch (_) {}
    const err  = new Error("E-posta adresiniz henüz doğrulanmamış. Lütfen mail kutunuzu kontrol edin.");
    err.code   = "__email_not_verified__";
    throw err;
  }

  await checkBlockStatus(user);
  try { await update(ref(db, `users/${user.uid}`), { lastLogin: Date.now(), emailVerified: true }); } catch (_) {}
  return user;
}

// ─── Google ile Giriş ─────────────────────────────────────────────────────────
// AKIŞ:
//   1. Google popup ile Firebase Auth girişi
//   2. Ban kontrolü
//   3. Aynı email ile mevcut bir email/şifre hesabı var mı kontrol et
//      → Varsa: Google hesabını o hesaba bağla (link) — tek kimlik
//   4. /users/$uid kaydı varsa → lastLogin güncelle
//   5. Yoksa → profil yaz (flag ile korunmuş — onAuthStateChanged yarış yaratmaz)
// _googleProfileWriting: profil yazılırken AuthContext'in erken tetiklenmesini önler
export let _googleProfileWriting = false;

export async function loginWithGoogle() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });

  let user;

  // Google popup
  try {
    const result = await signInWithPopup(auth, provider);
    user = result.user;
  } catch (popupErr) {
    // Aynı email farklı provider hatası → link gerekli
    if (
      popupErr.code === "auth/account-exists-with-different-credential" ||
      popupErr.code === "auth/email-already-in-use"
    ) {
      const pendingCred = GoogleAuthProvider.credentialFromError(popupErr);
      const email       = popupErr.customData?.email;
      if (!email || !pendingCred) throw popupErr;

      const err       = new Error("email_link_required");
      err.code        = "__link_required__";
      err.email       = email;
      err.pendingCred = pendingCred;
      throw err;
    }
    throw popupErr;
  }

  await checkBlockStatus(user);

  const snap    = await get(ref(db, `users/${user.uid}`));
  const isAdmin = MODERATOR_UIDS.includes(user.uid);

  const existingData   = snap.exists() ? snap.val() : null;
  const hasFullProfile = !!existingData?.displayName;

  if (hasFullProfile) {
    // ── Mevcut tam profilli kullanıcı — lastLogin güncelle ───────────────
    try {
      await update(ref(db, `users/${user.uid}`), {
        lastLogin:     Date.now(),
        emailVerified: true,
      });
    } catch (_) {}
    return user;
  }

  // ── İlk Google girişi — profil yaz ───────────────────────────────────────
  // Flag'i kaldır: AuthContext bu süreçte onAuthStateChanged'i görmezden gelir
  _googleProfileWriting = true;
  try {
    const googleName  = (user.displayName || user.email.split("@")[0]).trim();
    const key         = toUsernameKey(googleName);

    let finalName = googleName;
    if (key) {
      const nameSnap = await get(ref(db, `usernames/${key}`));
      if (nameSnap.exists() && nameSnap.val()?.uid !== user.uid) {
        finalName = `${googleName}${Math.floor(Math.random() * 9000) + 1000}`;
      }
    }
    const finalKey = toUsernameKey(finalName);
    const photoURL = randomDefaultPhoto();
    const createdAt = Date.now();

    await set(ref(db, `users/${user.uid}`), {
      uid:           user.uid,
      email:         user.email,
      displayName:   finalName,
      photoURL,
      role:          isAdmin ? "admin" : "user",
      isAdmin,
      isBanned:      false,
      status:        "active",
      emailVerified: true,
      createdAt,
      lastLogin:     Date.now(),
      language:      "en",
      nationality:   "",
      bio:           "",
    });

    await syncPublicProfile(user, {
      displayName: finalName,
      photoURL,
      bio: "",
      nationality: "",
      createdAt,
    });

    if (finalKey) {
      await set(ref(db, `usernames/${finalKey}`), { uid: user.uid, displayName: finalName });
    }

    try { await updateProfile(user, { displayName: finalName }); } catch (_) {}
  } finally {
    // Profil yazımı bitti (hata olsa bile) — flag'i sıfırla
    _googleProfileWriting = false;
  }

  return user;
}

// ─── Google Hesabı Bağlama ────────────────────────────────────────────────────
// __link_required__ hatası yakalandığında AuthContext bu fonksiyonu çağırır.
// Kullanıcı email/şifresiyle giriş yapar, Google credential'ı o hesaba bağlanır.
export async function linkGoogleToEmail({ email, password, pendingCred }) {
  // Önce email/şifre ile giriş yap
  const { user } = await signInWithEmailAndPassword(auth, email, password);

  // Google credential'ı bu hesaba bağla
  await linkWithCredential(user, pendingCred);

  // Kullanıcıya bildir — mevcut hesaba Google eklendi
  return user;
}

// ─── Hesap Silme ──────────────────────────────────────────────────────────────
// Sıra önemli:
//   1. Google hesabıysa önce reauth — token taze olmazsa deleteUser reddeder
//   2. /users/$uid sil
//   3. /usernames/$key sil
//   4. Storage fotoğrafı sil (Firebase Storage'daysa)
//   5. deleteUser → Auth kaydını imha et
//   6. signOut → oturumu kapat (deleteUser bazen oturumu kapatmaz)
export async function deleteUserAccount() {
  let user = auth.currentUser;
  if (!user) {
    const err  = new Error("Oturum bulunamadı. Lütfen tekrar giriş yapın.");
    err.code   = "auth/no-current-user";
    throw err;
  }

  const uid           = user.uid;
  const originalEmail = user.email;

  // ── Google hesabı: token'ı tazele, hesap seçtirme ──────────────────────
  // signInWithPopup ile mevcut hesabın credential'ını al (login_hint ile
  // doğrudan o hesaba gider, seçim ekranı açılmaz).
  // Ardından reauthenticateWithCredential ile sessizce token'ı tazele.
  const isGoogleUser = user.providerData?.some(p => p.providerId === "google.com");
  if (isGoogleUser) {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ login_hint: originalEmail });
    try {
      // login_hint mevcut hesabı doğrudan seçer — kullanıcı bir şey görmez
      const popupResult = await signInWithPopup(auth, provider);
      const credential  = GoogleAuthProvider.credentialFromResult(popupResult);
      // Bu credential ile asıl user'ı reauth et
      const reauthResult = await reauthenticateWithCredential(user, credential);
      user = reauthResult.user ?? auth.currentUser;
    } catch (reauthErr) {
      if (
        reauthErr.code === "auth/popup-closed-by-user"   ||
        reauthErr.code === "auth/cancelled-popup-request" ||
        reauthErr.code === "auth/user-cancelled"
      ) {
        const e  = new Error("Doğrulama iptal edildi. Hesap silinmedi.");
        e.code   = "auth/reauth-cancelled";
        throw e;
      }
      throw reauthErr;
    }
    if (!user) {
      const err  = new Error("Reauth sonrası oturum bulunamadı.");
      err.code   = "auth/no-current-user";
      throw err;
    }
  }

  // ── DB verilerini oku ────────────────────────────────────────────────────
  let displayName       = "";
  let photoURL          = "";
  let savedUserData     = null;
  let savedPublicProfile = null;
  let savedUsernameData = null;

  try {
    const userSnap = await get(ref(db, `users/${uid}`));
    if (userSnap.exists()) {
      savedUserData = userSnap.val();
      displayName   = savedUserData.displayName ?? user.displayName ?? "";
      photoURL      = savedUserData.photoURL    ?? "";
    }
  } catch (_) {}

  try {
    const publicProfileSnap = await get(ref(db, `publicProfiles/${uid}`));
    if (publicProfileSnap.exists()) savedPublicProfile = publicProfileSnap.val();
  } catch (_) {}

  const key = toUsernameKey(displayName);

  try {
    const unameSnap = await get(ref(db, `usernames/${key}`));
    if (unameSnap.exists()) savedUsernameData = unameSnap.val();
  } catch (_) {}

  // ── DB'yi sil (token hâlâ geçerli) ──────────────────────────────────────
  try { await remove(ref(db, `users/${uid}`)); } catch (_) {}
  try { await remove(ref(db, `publicProfiles/${uid}`)); } catch (_) {}
  if (key) {
    try { await remove(ref(db, `usernames/${key}`)); } catch (_) {}
  }

  // ── Storage fotoğrafı sil ───────────────────────────────────────────────
  if (photoURL && isFirebaseStorageUrl(photoURL)) {
    const path = extractStoragePath(photoURL);
    if (path) {
      try { await deleteObject(storageRef(storage, path)); } catch (_) {}
    }
  }

  // ── Firebase Auth kaydını sil ───────────────────────────────────────────
  try {
    await deleteUser(user);
  } catch (authErr) {
    // Auth silinemedi — DB'yi geri yükle
    if (savedUserData) {
      try { await set(ref(db, `users/${uid}`), savedUserData); } catch (_) {}
    }
    if (savedPublicProfile) {
      try { await set(ref(db, `publicProfiles/${uid}`), savedPublicProfile); } catch (_) {}
    }
    if (key && savedUsernameData) {
      try { await set(ref(db, `usernames/${key}`), savedUsernameData); } catch (_) {}
    }

    // Anlamlı hata kodu ile fırlat
    if (authErr.code === "auth/requires-recent-login") {
      const e  = new Error("Güvenlik nedeniyle tekrar giriş yapıp bu işlemi deneyin.");
      e.code   = "auth/requires-recent-login";
      throw e;
    }
    throw authErr;
  }

  // ── Oturumu kapat ────────────────────────────────────────────────────────
  try { await signOut(auth); } catch (_) {}
}

// ─── Çıkış ────────────────────────────────────────────────────────────────────
export async function logout() {
  await signOut(auth);
}

// ─── Şifre Sıfırlama ──────────────────────────────────────────────────────────
export async function resetPassword(email) {
  await sendPasswordResetEmail(auth, email);
}
